import { ApplicationController, useController } from '@aha-app/mvc';
import { useEffect, useLayoutEffect } from 'react';

// @ts-ignore
import Shepherd from 'javascripts/shepherd';
import { UserPreference } from 'components/framework/models';
import {
  getLocalStoragePreference,
  setLocalStoragePreference,
} from 'javascripts/utils';

const TOUR_STATE_PREFERENCE_KEY = 'getting_started.notebooks.tour_seen';

interface State {
  tour: Shepherd;
}

interface Props {}
/**
 * You can import the controller if you need to manipulate the tour
 * It's unclear if that will be necessary yet.
 */
export class ToursController extends ApplicationController<State, Props> {
  static initialState: State = {
    tour: null,
  };

  async initialize() {
    this.clearTour();
  }

  clearTour() {
    const tour = this.state.tour;
    if (tour) {
      tour.cancel();
      this.state.tour = null;
      $('.tour-faded').removeClass('tour-faded');
    }
  }

  async initTour({ tourSteps, tourName }) {
    // Return nothing if there are no steps
    if (!tourSteps.length) {
      return;
    }

    if (window.currentAccount.isGuest) {
      return;
    }

    if (window.AhaToursDisabled) {
      return; // tours disabled by default in capybara, opt-in with :enable_tours flag
    }

    this.state.tour = this._createTour();
    this._prepareTourStep(tourSteps);

    if (
      this._welcomeModalShowing() ||
      this._accountCreatedBeforeToursExisted(tourName)
    ) {
      return;
    }

    const tourHasBeenSeen = await this._tourHasBeenSeen(tourName);

    if (!tourHasBeenSeen || this._tourEnabledFromQueryParams()) {
      this.actionStartTour();
      UserPreference.setUserPreference(
        `${TOUR_STATE_PREFERENCE_KEY}.${tourName}`,
        'true'
      );
      setLocalStoragePreference(
        `${TOUR_STATE_PREFERENCE_KEY}.${tourName}`,
        String(tourHasBeenSeen)
      );
    }
  }

  actionStartTour() {
    // reset all fades on start
    $('.tour-faded').removeClass('tour-faded');
    this.state.tour.start();
  }

  private async _tourHasBeenSeen(tourName: String) {
    const cachedTourState = getLocalStoragePreference(
      `${TOUR_STATE_PREFERENCE_KEY}.${tourName}`
    );

    if (cachedTourState === 'true') {
      return true;
    } else {
      const tourHasBeenSeen = await UserPreference.select('value').findBy({
        name: `${TOUR_STATE_PREFERENCE_KEY}.${tourName}`,
      });
      setLocalStoragePreference(
        `${TOUR_STATE_PREFERENCE_KEY}.${tourName}`,
        String(tourHasBeenSeen?.value)
      );
      return tourHasBeenSeen?.value;
    }
  }

  private _createTour() {
    return new Shepherd.Tour({
      defaults: {
        classes: 'shepherd-theme-arrows',
        cancelIcon: {
          enabled: true,
        },
        when: {
          show: function () {
            $('.tour-faded').removeClass('tour-faded');
            this.options.makeOpaque.forEach(selector => {
              $(selector).addClass('tour-faded');
            });
          },
          hide: function () {
            $('.tour-faded').removeClass('tour-faded');
          },
        },
      },
      useModalOverlay: true,
    });
  }

  private _prepareTourStep(tourSteps: any) {
    const controller = this;
    const parsedStepsforAction = tourSteps.map(step => {
      const {
        buttons,
        waitForElement,
        attachTo: { element },
      } = step;
      // we wait for attachTo by default. You can supply more waitForElements as needed
      const waitSelector = [waitForElement, element].filter(Boolean).join(', ');

      step.beforeShowPromise = function (a) {
        return new Promise(resolve => {
          controller._waitForElement(waitSelector, resolve);
        });
      };

      if (buttons) {
        step.buttons = buttons.map(button => {
          const { action, classes, disabled, label, secondary, text, type } =
            button;
          return {
            action: type ? this.state.tour[type] : action,
            classes,
            disabled,
            label,
            secondary,
            text,
            type,
          };
        });
      }

      return step;
    });

    parsedStepsforAction.forEach((step: any) => this.state.tour.addStep(step));
  }

  private _waitForElement(selector: String, resolve: FrameRequestCallback) {
    const el = $(selector);
    if (el.length > 0) {
      // console.log(`Finished for ${selector}`);
      if (el.parents('#modal-window').length > 0) {
        setTimeout(resolve, 450);
      } else {
        window.requestAnimationFrame(resolve);
      }
    } else {
      setTimeout(() => this._waitForElement(selector, resolve), 50);
    }
  }

  private _tourEnabledFromQueryParams() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('tour') === 'true';
  }

  private _welcomeModalShowing() {
    return $('.quick-start-guide--notebooks.in').length > 0;
  }

  private _accountCreatedBeforeToursExisted(tourName: string) {
    const createdBeforeTours =
      new Date(window.currentAccount.createdAt) < new Date('2023-09-26');

    if (createdBeforeTours) {
      // eventually we can remove this once we're confident
      // these prefs have been set for enough existing accounts
      UserPreference.setUserPreference(
        `${TOUR_STATE_PREFERENCE_KEY}.${tourName}`,
        'true'
      );
    }
    return createdBeforeTours;
  }
}

/**
 * The main public hook for wiring tours on your page
 * @param tourSteps the Shepherd.js formatted array of POJO steps
 */
const useTours = function ({ tourSteps, tourName }) {
  const tourController = useController(ToursController);
  useLayoutEffect(() => {
    tourController.initTour({ tourSteps, tourName });
  }, [tourController, tourSteps, tourName]);

  /**
   * Listen for tour starts from outside the main DesktopNotes app
   * This is used by the quick start guide, which currently lives outside the app
   *  unclear if worth bringing it (and the whole getting started header with it) into the app
   */
  useEffect(() => {
    document.addEventListener(
      'aha.notebooks.start-tour',
      tourController.actionStartTour
    );

    return () => {
      document.removeEventListener(
        'aha.notebooks.start-tour',
        tourController.actionStartTour
      );
    };
  }, [tourController.actionStartTour]);
};

export default useTours;
